import 'react-toastify/dist/ReactToastify.css'
import './EasySoftwareEditorContainer.scss'

import React, { FunctionComponent, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useEditorStatus } from 'Root/Shared/utils/edit/useEditorStatus'

import EasySoftwareInputEditor from '../EasySoftwareInputEditor/EasySoftwareInputEditor'
import EasySoftwareWysiwygEditor from '../EasySoftwareWysiwygEditor/EasySoftwareWysiwygEditor'
import { usePuxEditor } from '../PuxEditor/PuxEditorContext'
import type { SaveResult } from '../PuxEditor/PuxEditorState'
import PuxWysiwyg, { PuxWysiwygHtmlBodyType } from '../PuxWysiwyg/PuxWysiwyg'

const CLASS_BLACKLIST = [
  `ql-ui`,
  `markdown`,
  `prose`,
  `w-full`,
  `break-words`,
  `dark:prose-invert`,
  `light`,
]

export interface EasySoftwareEditorContainerType {
  content: PuxWysiwygHtmlBodyType | string
  wysiwygId?: string
  className?: string
  inline?: boolean
  small?: boolean
  pageContentItemId: string
  editorPath: string // Used in news, to set unique id for perex and content wysiwygs
  saveText?: boolean
  type?: 'wysiwyg' | 'input'
}

const EasySoftwareEditorContainer: FunctionComponent<EasySoftwareEditorContainerType> = (
  props
) => {
  const isEditor = useEditorStatus()
  const { store, session } = usePuxEditor()
  const fieldId = JSON.stringify([props.pageContentItemId, props.editorPath])
  const editorInitialized = session?.id === fieldId
  const fieldName =
    props.editorPath
      ?.split(`,`)
      .filter((part) => part && ![`html`, `text`].includes(part))
      .pop() ?? `Content`
  const fieldLabel = fieldName
    .replace(/([a-z])([A-Z])/g, `$1 $2`)
    .replace(/^./, (letter) => letter.toUpperCase())
  const [wysiwygValue, setWysiwygValue] = useState(props.content)
  const regexPattern = new RegExp(
    `\\b(${CLASS_BLACKLIST.join(`|`)})\\b\\s*`,
    `g`
  )
  const wysiwygId = props.wysiwygId ?? props.pageContentItemId

  useEffect(
    () => () => {
      if (store.getState()?.id === fieldId) store.cancel()
    },
    [store, fieldId]
  )

  async function saveEditor(value: string): Promise<SaveResult> {
    if (!props.pageContentItemId || !props.editorPath) {
      return { ok: false, error: `Editor is missing parameters.` }
    }
    value = value
      .replace(regexPattern, ``)
      .trim()
      .replace(/\sclass=""/g, ``)
    const savedValue = props.saveText
      ? value.replace(/<(\/*)p[^>]*>/g, ``)
      : value
    try {
      const response = await fetch(
        `${process.env.GATSBY_ORCHARD_API_URL}/puxapi/contentitem/editfield`,
        {
          method: `POST`,
          headers: { 'Content-Type': `application/json` },
          credentials: `include`,
          body: JSON.stringify({
            contentitemid: props.pageContentItemId,
            path: props.editorPath,
            value: savedValue,
          }),
        }
      )
      if (!response.ok) {
        return {
          ok: false,
          error:
            response.status === 401 || response.status === 403
              ? `You are not authorized to edit this culture.`
              : `Unable to save. Please try again.`,
        }
      }
      setWysiwygValue(
        props.type === `input` ? savedValue : { html: savedValue }
      )
      toast.success(`Content saved.`, { position: `bottom-right` })
      return { ok: true }
    } catch {
      return {
        ok: false,
        error: `Unable to save. Check your connection and try again.`,
      }
    }
  }

  const contentElement =
    props.type === `input` ? (
      <div className='Input'>
        {typeof wysiwygValue === `string`
          ? wysiwygValue
          : wysiwygValue?.toString() ?? ``}
      </div>
    ) : (
      <PuxWysiwyg
        content={
          typeof wysiwygValue === `string`
            ? { html: wysiwygValue }
            : wysiwygValue
        }
        wysiwygId={wysiwygId}
        className={props.className}
        inline={props.inline}
        small={props.small}
      />
    )

  if (isEditor && editorInitialized) {
    return (
      <>
        {props.type === `input` ? (
          <EasySoftwareInputEditor
            path={props.editorPath}
            pageContentItemId={props.pageContentItemId}
            wysiwygId={wysiwygId}
            className={props.className}
            inline={props.inline}
            small={props.small}
            fieldId={fieldId}
            saveEditor={saveEditor}
          />
        ) : (
          <EasySoftwareWysiwygEditor
            path={props.editorPath}
            pageContentItemId={props.pageContentItemId}
            wysiwygId={wysiwygId}
            className={props.className}
            inline={props.inline}
            small={props.small}
            fieldId={fieldId}
            saveEditor={saveEditor}
          />
        )}
      </>
    )
  } else if (isEditor) {
    return (
      <div
        className='Editor-container'
        role='button'
        tabIndex={0}
        aria-label={`Edit ${fieldLabel}`}
        onKeyDown={(event) => {
          if (
            event.target === event.currentTarget &&
            (event.key === `Enter` || event.key === ` `)
          ) {
            event.preventDefault()
            event.currentTarget.click()
          }
        }}
        onClick={() => {
          if (store.getState()) {
            toast.warning(`Close active editor, to open new instance.`, {
              position: `bottom-right`,
            })
            return
          }

          if (
            typeof wysiwygValue === `object` &&
            wysiwygValue?.html?.includes(`<table`)
          ) {
            toast.warning(`Wysiwyg contains table, open CMS to edit.`, {
              position: `bottom-right`,
            })
            return
          }

          store.open({
            id: fieldId,
            kind: props.type ?? `wysiwyg`,
            label: fieldLabel,
          })
        }}
      >
        {contentElement}
      </div>
    )
  } else {
    return contentElement
  }
}

export default EasySoftwareEditorContainer
